import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message,
  Timeline,
  Card,
  Space,
  Divider,
  Typography,
  Row,
  Col,
  Tag,
  Statistic,
} from "antd";
import {
  DollarOutlined,
  HistoryOutlined,
  EditOutlined,
  CalendarOutlined,
  UserOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import unitModel from "../lib/firebase/unitModel";
import {
  updateTenantRent,
  getTenantRentHistory,
} from "../lib/firebase/tenantModel";

const { Option } = Select;
const { Title, Text } = Typography;

const RentManagementModal = ({
  isOpen,
  onClose,
  onSuccess,
  type, // 'unit' or 'tenant'
  itemId,
  itemName,
  currentRent,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rentHistory, setRentHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("update");

  const loadRentHistory = useCallback(async () => {
    try {
      setLoading(true);
      let history = [];

      if (type === "unit") {
        history = await unitModel.getRentHistory(itemId);
      } else if (type === "tenant") {
        history = await getTenantRentHistory(itemId);
      }

      setRentHistory(history || []);
    } catch (error) {
      console.error("Error loading rent history:", error);
      message.error("Failed to load rent history");
    } finally {
      setLoading(false);
    }
  }, [type, itemId]);

  useEffect(() => {
    if (isOpen && itemId) {
      loadRentHistory();
      form.setFieldsValue({ newRent: currentRent });
    }
  }, [isOpen, itemId, currentRent, form, loadRentHistory]);

  const handleRentUpdate = async (values) => {
    try {
      setLoading(true);

      if (type === "unit") {
        await unitModel.updateRent(itemId, values.newRent, values.reason);
      } else if (type === "tenant") {
        await updateTenantRent(itemId, values.newRent, values.reason);
      }

      message.success("Rent updated successfully");
      await loadRentHistory();
      onSuccess?.();
    } catch (error) {
      console.error("Error updating rent:", error);
      message.error("Failed to update rent");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getReasonColor = (reason) => {
    switch (reason) {
      case "market_adjustment":
        return "blue";
      case "promotion":
        return "green";
      case "lease_renewal":
        return "orange";
      case "rent_increase":
        return "red";
      default:
        return "default";
    }
  };

  const getReasonLabel = (reason) => {
    switch (reason) {
      case "market_adjustment":
        return "Market Adjustment";
      case "promotion":
        return "Promotion";
      case "lease_renewal":
        return "Lease Renewal";
      case "rent_increase":
        return "Rent Increase";
      default:
        return "Rent Adjustment";
    }
  };

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined />
          <span>Rent Management - {type === "unit" ? "Unit" : "Tenant"}</span>
        </Space>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <StyledModalContent>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card>
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <div>
                  <Title level={4}>
                    <Space>
                      {type === "unit" ? <HomeOutlined /> : <UserOutlined />}
                      {itemName}
                    </Space>
                  </Title>
                  <Text type="secondary">
                    Current Rent: ${currentRent?.toLocaleString()}
                  </Text>
                </div>

                <Divider />

                <div>
                  <Space>
                    <Button
                      type={activeTab === "update" ? "primary" : "default"}
                      onClick={() => setActiveTab("update")}
                      icon={<EditOutlined />}
                    >
                      Update Rent
                    </Button>
                    <Button
                      type={activeTab === "history" ? "primary" : "default"}
                      onClick={() => setActiveTab("history")}
                      icon={<HistoryOutlined />}
                    >
                      Rent History
                    </Button>
                  </Space>
                </div>

                {activeTab === "update" && (
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleRentUpdate}
                  >
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="newRent"
                          label="New Rent Amount"
                          rules={[
                            {
                              required: true,
                              message: "Please enter new rent amount",
                            },
                            {
                              type: "number",
                              min: 0,
                              message: "Rent must be positive",
                            },
                          ]}
                        >
                          <InputNumber
                            prefix="$"
                            style={{ width: "100%" }}
                            placeholder="Enter new rent amount"
                            formatter={(value) =>
                              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                            }
                            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="reason"
                          label="Reason for Change"
                          rules={[
                            {
                              required: true,
                              message: "Please select a reason",
                            },
                          ]}
                        >
                          <Select placeholder="Select reason">
                            <Option value="market_adjustment">
                              Market Adjustment
                            </Option>
                            <Option value="promotion">
                              Promotion/Discount
                            </Option>
                            <Option value="lease_renewal">Lease Renewal</Option>
                            <Option value="rent_increase">Rent Increase</Option>
                            <Option value="rent_adjustment">
                              General Adjustment
                            </Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item>
                      <Space>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                        >
                          Update Rent
                        </Button>
                        <Button onClick={onClose}>Cancel</Button>
                      </Space>
                    </Form.Item>
                  </Form>
                )}

                {activeTab === "history" && (
                  <div>
                    <Title level={5}>Rent History</Title>
                    {rentHistory.length === 0 ? (
                      <Text type="secondary">No rent history available</Text>
                    ) : (
                      <Timeline>
                        {rentHistory.map((entry, index) => (
                          <Timeline.Item
                            key={index}
                            dot={
                              <CalendarOutlined style={{ color: "#1890ff" }} />
                            }
                          >
                            <div>
                              <Space>
                                <Text strong>
                                  ${entry.newRent?.toLocaleString()}
                                  {entry.previousRent && (
                                    <span
                                      style={{
                                        color: "#666",
                                        marginLeft: "8px",
                                      }}
                                    >
                                      (was $
                                      {entry.previousRent?.toLocaleString()})
                                    </span>
                                  )}
                                </Text>
                                <Tag color={getReasonColor(entry.reason)}>
                                  {getReasonLabel(entry.reason)}
                                </Tag>
                              </Space>
                              <div>
                                <Text
                                  type="secondary"
                                  style={{ fontSize: "12px" }}
                                >
                                  {formatDate(entry.date)}
                                </Text>
                              </div>
                            </div>
                          </Timeline.Item>
                        ))}
                      </Timeline>
                    )}
                  </div>
                )}
              </Space>
            </Card>
          </Col>
        </Row>
      </StyledModalContent>
    </Modal>
  );
};

const StyledModalContent = styled.div`
  .ant-timeline-item-content {
    margin-bottom: 16px;
  }

  .ant-statistic-title {
    color: #666;
    font-size: 14px;
  }

  .ant-statistic-content {
    color: #1890ff;
    font-size: 24px;
    font-weight: 600;
  }
`;

export default RentManagementModal;
